import type React from "react"
import { useMemo, useState, useEffect, useRef } from "react"
import type { ColumnDef } from "@tanstack/react-table"
import { CRUDPage } from "../../components/crudPage.tsx"
import { InventoryForm } from "../../components/forms/inventoryForm.tsx"
import { InventoryTransactionsModal } from "../../components/modals/InventoryTransactionsModal.tsx"
import { InventoryAssignmentsModal } from "../../components/modals/InventoryAssignmentsModal.tsx"
import { Modal } from "../../components/modal.tsx"
import axiosInstance from "../../utils/axiosConfig.ts"
import { getToken } from "../../utils/auth.ts"
import { Plus, Trash2, Pencil, Save, X } from "lucide-react"
import { useCompany } from "../../context/CompanyContext.tsx"
import { toast } from "../../utils/notify.ts"

interface InventoryItem {
  id: string
  item_type: string
  quantity: number
  vendor: string
  vendor_name: string
  unit_price?: number
  is_active: boolean
  company_id: string
  attributes: any
  created_at?: string
  updated_at?: string
}

interface InventoryItemType {
  id: string
  name: string
  description?: string | null
  is_default?: boolean
  is_active: boolean
}

const InventoryManagement: React.FC = () => {
  const { setPageTitle } = useCompany()
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([])
  const [showTransactionsModal, setShowTransactionsModal] = useState(false)
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
  const [showItemTypesModal, setShowItemTypesModal] = useState(false)
  const [itemTypes, setItemTypes] = useState<InventoryItemType[]>([])
  const [newItemType, setNewItemType] = useState({ name: "", description: "" })
  const [editingType, setEditingType] = useState<InventoryItemType | null>(null)
  const [editTypeData, setEditTypeData] = useState({ name: "", description: "" })
  const [itemTypesVersion, setItemTypesVersion] = useState(0)
  const modalContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const token = getToken()
        const response = await axiosInstance.get("/suppliers/list", {
          headers: { Authorization: `Bearer ${token}` },
        })
        setSuppliers(response.data)
        setPageTitle("Inventory")
      } catch (error) {
        console.error("Failed to fetch suppliers", error)
        setPageTitle("Inventory")
      }
    }

    fetchSuppliers()
  }, [setPageTitle])

  const fetchItemTypes = async () => {
    try {
      const token = getToken()
      const response = await axiosInstance.get("/inventory-item-types/list", {
        headers: { Authorization: `Bearer ${token}` },
      })
      setItemTypes(response.data || [])
    } catch (error) {
      console.error("Failed to fetch inventory item types", error)
      toast.error("Failed to load inventory item types")
    }
  }

  const openItemTypesModal = () => {
    setShowItemTypesModal(true)
    fetchItemTypes()
  }

  const closeItemTypesModal = () => {
    setShowItemTypesModal(false)
    setEditingType(null)
    setEditTypeData({ name: "", description: "" })
    setNewItemType({ name: "", description: "" })
    setItemTypesVersion((v) => v + 1)
  }

  const handleAddItemType = async () => {
    if (!newItemType.name.trim()) return
    try {
      const token = getToken()
      await axiosInstance.post("/inventory-item-types/add", newItemType, {
        headers: { Authorization: `Bearer ${token}` },
      })
      toast.success("Item type added")
      setNewItemType({ name: "", description: "" })
      await fetchItemTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add item type")
    }
  }

  const handleEditItemType = (type: InventoryItemType) => {
    setEditingType(type)
    setEditTypeData({ name: type.name, description: type.description || "" })
  }

  const handleCancelEdit = () => {
    setEditingType(null)
    setEditTypeData({ name: "", description: "" })
  }

  const handleUpdateItemType = async () => {
    if (!editingType || !editTypeData.name.trim()) return
    try {
      const token = getToken()
      await axiosInstance.put(`/inventory-item-types/update/${editingType.id}`, editTypeData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      toast.success("Item type updated")
      setEditingType(null)
      setEditTypeData({ name: "", description: "" })
      await fetchItemTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update item type")
    }
  }

  const handleDeleteItemType = async (type: InventoryItemType) => {
    if (type.is_default) {
      toast.error("Default item types cannot be deleted")
      return
    }
    if (!window.confirm(`Delete item type "${type.name}"?`)) return
    try {
      const token = getToken()
      await axiosInstance.delete(`/inventory-item-types/delete/${type.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      toast.success("Item type deleted")
      await fetchItemTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete item type")
    }
  }

  const columns = useMemo<ColumnDef<InventoryItem>[]>(
    () => [
      {
        header: "Item Type",
        accessorKey: "item_type",
      },
      {
        header: "Details",
        cell: ({ row }) => {
          const item = row.original
          const attributes = item.attributes || {}

          switch (item.item_type) {
            case "Fiber Cable":
              return "Fiber Cable"
            case "EtherNet Cable":
              return `Type: ${attributes.type || "N/A"}`
            case "Splitters":
              return "Splitter"
            case "ONT":
            case "ONU":
            case "Router":
            case "STB":
              return (
                <div>
                  <div>Serial: {attributes.serial_number || "N/A"}</div>
                  <div>Type: {attributes.type || "N/A"}</div>
                  <div>Model: {attributes.model || "N/A"}</div>
                </div>
              )
            case "Fibe OPTIC Patch Cord":
            case "Ethernet Patch Cord":
              return `Type: ${attributes.type || "N/A"}`
            case "Switches":
              return `Type: ${attributes.type || "N/A"}`
            case "Node":
              return `Type: ${attributes.type || "N/A"}`
            case "Dish":
              return (
                <div>
                  <div>MAC: {attributes.mac_address || "N/A"}</div>
                  <div>Type: {attributes.type || "N/A"}</div>
                </div>
              )
            case "Adopter":
              return (
                <div>
                  <div>Volt: {attributes.volt || "N/A"}</div>
                  <div>Amp: {attributes.amp || "N/A"}</div>
                </div>
              )
            case "Cable Ties":
              return (
                <div>
                  <div>Type: {attributes.type || "N/A"}</div>
                  <div>Model: {attributes.model || "N/A"}</div>
                </div>
              )
            case "Others":
              return "Other Item"
            default:
              return attributes.type || attributes.model || attributes.serial_number || "N/A"
          }
        },
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
      },
      {
        header: "Vendor",
        accessorKey: "vendor_name",
      },
      {
        header: "Unit Price",
        accessorKey: "unit_price",
        cell: ({ row }) =>
          row.original.unit_price != null ? `PKR${Number(row.original.unit_price).toFixed(2)}` : "N/A",
      },
      {
        header: "Transactions",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => {
              setSelectedItem(row.original)
              setShowTransactionsModal(true)
            }}
            className="px-2 py-1 bg-[#89A8B2] text-white rounded-md hover:bg-[#6f8a9a] transition-colors duration-200 text-sm"
          >
            View Transactions
          </button>
        ),
      },
      {
        header: "Assignments",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => {
              setSelectedItem(row.original)
              setShowAssignmentsModal(true)
            }}
            className="px-2 py-1 bg-[#89A8B2] text-white rounded-md hover:bg-[#6f8a9a] transition-colors duration-200 text-sm"
          >
            View Assignments
          </button>
        ),
      },
    ],
    [],
  )

  const validateBeforeSubmit = (formData: Partial<InventoryItem>) => {
    if (!formData.item_type) {
      return "Item type is required"
    }
    if (!formData.vendor) {
      return "Vendor is required"
    }
    if (formData.quantity == null || formData.quantity < 1) {
      return "Quantity must be at least 1"
    }

    const attributes = formData.attributes || {}

    if (formData.item_type === "EtherNet Cable" && !attributes.type) {
      return "Cable type is required"
    }

    if (["ONT", "ONU", "Router", "STB"].includes(formData.item_type || "")) {
      if (!attributes.serial_number) {
        return "Serial number is required"
      }
    }

    if (formData.item_type === "Dish" && !attributes.mac_address) {
      return "MAC address is required"
    }

    return null
  }

  return (
    <>
      <CRUDPage<InventoryItem>
        title="Inventory"
        endpoint="inventory"
        filterModuleKey="inventory"
        columns={columns}
        FormComponent={(props) => (
          <InventoryForm
            key={itemTypesVersion}
            {...props}
            suppliers={suppliers}
            onManageItemTypes={openItemTypesModal}
          />
        )}
        validateBeforeSubmit={validateBeforeSubmit}
      />

      <Modal isVisible={showItemTypesModal} onClose={closeItemTypesModal} title="Manage Item Types" size="md">
        <div
          ref={modalContentRef}
          className="space-y-4 max-h-[70vh] overflow-y-auto"
          style={{ scrollBehavior: "smooth" }}
        >
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">
              {editingType ? "Edit Item Type" : "Add New Item Type"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
                  Name *
                </label>
                <input
                  type="text"
                  value={editingType ? editTypeData.name : newItemType.name}
                  onChange={(e) =>
                    editingType
                      ? setEditTypeData((prev) => ({ ...prev, name: e.target.value }))
                      : setNewItemType((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter item type name"
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-[#F8FAFB] text-slate-800 focus:bg-white focus:border-[#2A5C8A] focus:outline-none focus:ring-1 focus:ring-[#2A5C8A]/25"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
                  Description
                </label>
                <textarea
                  value={editingType ? editTypeData.description : newItemType.description}
                  onChange={(e) =>
                    editingType
                      ? setEditTypeData((prev) => ({ ...prev, description: e.target.value }))
                      : setNewItemType((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Enter description"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-[#F8FAFB] text-slate-800 focus:bg-white focus:border-[#2A5C8A] focus:outline-none focus:ring-1 focus:ring-[#2A5C8A]/25"
                />
              </div>
              <div className="flex gap-2">
                {editingType ? (
                  <>
                    <button
                      type="button"
                      onClick={handleUpdateItemType}
                      disabled={!editTypeData.name.trim()}
                      className="flex items-center gap-2 h-9 px-4 text-sm font-medium bg-[#2A5C8A] text-white rounded-md shadow-sm hover:bg-[#1e4568] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save size={16} />
                      Update Type
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex items-center gap-2 h-9 px-4 text-sm bg-slate-gray text-white rounded-md hover:bg-slate-gray/90"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleAddItemType}
                    disabled={!newItemType.name.trim()}
                    className="flex items-center gap-2 h-9 px-4 text-sm font-medium bg-[#2A5C8A] text-white rounded-md shadow-sm hover:bg-[#1e4568] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                    Add Type
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Existing Item Types</h3>
            <div className="space-y-2">
              {itemTypes.length === 0 ? (
                <p className="text-center text-slate-gray py-4 text-sm">No item types found</p>
              ) : (
                itemTypes.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-3 bg-white border border-slate-gray/20 rounded-lg gap-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-deep-ocean text-sm">{type.name}</h4>
                        {type.is_default && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                            Default
                          </span>
                        )}
                        {!type.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-coral-red/10 text-coral-red rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      {type.description && <p className="text-xs text-slate-gray mt-0.5">{type.description}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditItemType(type)}
                        className="p-1.5 text-electric-blue hover:bg-electric-blue/10 rounded transition-colors"
                        title="Edit item type"
                      >
                        <Pencil size={16} />
                      </button>
                      {!type.is_default && (
                        <button
                          type="button"
                          onClick={() => handleDeleteItemType(type)}
                          className="p-1.5 text-coral-red hover:bg-coral-red/10 rounded transition-colors"
                          title="Delete item type"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>

      {showTransactionsModal && (
        <InventoryTransactionsModal
          isVisible={showTransactionsModal}
          onClose={() => setShowTransactionsModal(false)}
          inventoryItemId={selectedItem!.id}
        />
      )}
      {showAssignmentsModal && (
        <InventoryAssignmentsModal
          isVisible={showAssignmentsModal}
          onClose={() => setShowAssignmentsModal(false)}
          inventoryItem={selectedItem!}
        />
      )}
    </>
  )
}

export default InventoryManagement
